import {
  supabase,
  Sentry,
  isDev,
  SupabaseError,
  withRetry,
  shouldRethrow,
  describeSupabaseFailure,
} from './shared'
import {
  isClientNetworkFailure,
  isDownloadRateLimited,
  isStatementTimeout,
  shouldReportTransientOnce,
} from './expected-failure'
import { downloadAccountPath, downloadAccountTarget } from '../download-accounting'
import type { Sound, Category } from '../supabase-types'
import { normalizeAudioSourceUrl } from '../audio-url-normalization'
import { pickDiverseSounds } from '../featured-sounds'
import { stripFillerWords } from '../search-filler-words'

// Lightweight column set for list pages — excludes waveform_data (large JSON array)
// to reduce SSR payload by ~50-80%. AudioWaveform generates deterministic fallbacks from soundId.
export const LIST_COLUMNS = 'id,slug,title,description,category,duration,size,format,downloads,featured,file_url,created_at,updated_at,tesla_filename,volume_db,original_volume_db,is_normalized,tags,is_premium,preview_url,is_active,creator_name,source_reference,likes,dislikes,compatible_models'

function normalizeSoundAudioUrls<T extends { file_url?: unknown; preview_url?: unknown }>(sound: T): T {
  if (!sound) {
    return sound
  }

  const normalized = { ...sound }
  let changed = false

  if (typeof sound.file_url === 'string') {
    const fileUrl = normalizeAudioSourceUrl(sound.file_url)
    if (fileUrl !== sound.file_url) {
      normalized.file_url = fileUrl
      changed = true
    }
  }

  if (typeof sound.preview_url === 'string') {
    const previewUrl = normalizeAudioSourceUrl(sound.preview_url)
    if (previewUrl !== sound.preview_url) {
      normalized.preview_url = previewUrl
      changed = true
    }
  }

  if (!changed) {
    return sound
  }

  return normalized
}

function normalizeSoundList<T extends { file_url?: unknown; preview_url?: unknown }>(sounds: T[] | null | undefined): T[] {
  return (sounds || []).map(normalizeSoundAudioUrls)
}

/**
 * The PostgREST `or=` string a curated collection's search terms resolve to:
 * each term matched as a literal substring against title OR description.
 *
 * Extracted so the collection PAGE and any COUNT of that collection are
 * answering the same question — a listing that says "45 sounds" over a page
 * showing 82 is a fabricated figure by drift rather than by intent.
 *
 * LIKE metacharacters and PostgREST filter-string delimiters are escaped so
 * a term is never interpreted as a pattern or as extra filter syntax.
 */
export function curatedSearchTermFilter(searchTerms: readonly string[]): string {
  return searchTerms
    .flatMap(term => {
      const safe = term.replace(/[\\%_,()]/g, '\\$&')
      return [`title.ilike.%${safe}%`, `description.ilike.%${safe}%`]
    })
    .join(',')
}

// Enhanced database operations
export const soundsApi = {
  /**
   * Fetch sounds with filtering, sorting, and pagination.
   *
   * Sort options (all secondary-ordered by `id` for stable pagination):
   * - `downloads` desc — "Most Downloaded" (default UI sort, highest download count first)
   * - `created_at` desc — "Newest" (recent uploads first)
   * - `created_at` asc — "Oldest"
   * - `title` asc/desc — alphabetical
   * - `duration` asc/desc — shortest / longest
   * - `volume_db` asc/desc — quietest / loudest
   *
   * When `search` is provided, sorts are applied server-side via the
   * `search_sounds_ranked` RPC; otherwise applied via Supabase order chains.
   * Defaults: sortBy = 'created_at', sortOrder = 'desc'.
   */
  async getSounds(options: {
    /**
     * Single DB category name, or an array for umbrella pages whose sounds
     * span several fine-grained DB categories (e.g. /sounds/category/games →
     * Modern Gaming + Retro Gaming + Mario & Nintendo + …). Arrays are only
     * honored on the direct-query path; the ranked-search RPC takes a single
     * category, so array filters are dropped there (search UI never passes one).
     */
    category?: string | string[]
    compatibility?: string
    compatibleModels?: string   // Tesla model name: 'Model 3', 'Model Y', 'Model S', 'Model X', 'Cybertruck'
    volumeRange?: [number, number]
    tags?: string[]
    sortBy?: 'created_at' | 'downloads' | 'title' | 'duration' | 'volume_db'
    sortOrder?: 'asc' | 'desc'
    limit?: number
    offset?: number
    search?: string
    durationRange?: 'all' | '0-2' | '2-5' | '5+'
    fileSizeRange?: 'all' | '<500' | '500-1000' | '1000+'
    teslaCompatible?: boolean
    premiumOnly?: boolean
    columns?: string  // Supabase select string; defaults to '*'. Use LIST_COLUMNS to exclude heavy columns.
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<Sound[]> {
    const debugLog = (message: string, data?: any) => {
      if (process.env.NODE_ENV === 'development') {
        const timestamp = new Date().toLocaleTimeString()
        if (data !== undefined) {
          console.log(`[${timestamp}] [soundsApi.getSounds] ${message}:`, data)
        } else {
          console.log(`[${timestamp}] [soundsApi.getSounds] ${message}`)
        }
      }
    }

    debugLog('Called with options', options)

    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // If search is provided, use the ranked full-text search RPC
        // This searches title (high weight), description (lower weight), and tags (lowest weight)
        if (options.search && options.search.trim()) {
          const searchTerm = options.search.trim().replace(/[<>]/g, '')
          debugLog('Using FTS ranked search', searchTerm)

          // Calculate duration range values for RPC
          let durationMin: number | null = null
          let durationMax: number | null = null
          if (options.durationRange === '0-2') {
            durationMin = 0
            durationMax = 2
          } else if (options.durationRange === '2-5') {
            durationMin = 2
            durationMax = 5
          } else if (options.durationRange === '5+') {
            durationMin = 5
          }

          // Bound once so this closure keeps the null-narrowing from the guard
          // at the top of the retry body.
          const db = supabase
          const runRankedSearch = (term: string) =>
            db.rpc('search_sounds_ranked', {
              search_query: term,
              category_filter: typeof options.category === 'string' && options.category !== 'All' ? options.category : null,
              duration_min: durationMin,
              duration_max: durationMax,
              tesla_compatible: options.teslaCompatible || false,
              sort_by: options.sortBy || 'rank',
              sort_order: options.sortOrder || 'desc',
              result_limit: options.limit || 50,
              result_offset: options.offset || 0,
              p_model_filter: options.compatibleModels || null
            })

          let { data, error } = await runRankedSearch(searchTerm)

          // Filler-word fallback (TES-2229). Full text needs every lexeme in the
          // query to be present, so `hee hee sound effect` fails for the same
          // reason the ILIKE route's contiguous-substring match does: "sound"
          // and "effect" are not in the title. This is the surface the issue is
          // named after — /sounds drives `search_sounds_ranked` straight from
          // the browser, it does not go through /api/sounds — so the fallback
          // has to exist in both backends or the library search keeps handing
          // Google visitors an empty page.
          //
          // Gated on a first page that came back empty: `offset` guards against
          // reinterpreting a real page-N-past-the-end as a failed search, and
          // an empty page 1 is the only state a visitor ever reads as "you have
          // nothing". `stripFillerWords` returns null when nothing was stripped
          // or when everything was, so a meaningless query stays empty.
          if (!error && (data as Sound[] | null)?.length === 0 && !options.offset) {
            const strippedTerm = stripFillerWords(searchTerm)
            if (strippedTerm) {
              debugLog('Empty FTS result, retrying without filler words', strippedTerm)
              const retried = await runRankedSearch(strippedTerm)
              if (!retried.error && (retried.data as Sound[] | null)?.length) {
                data = retried.data
              }
            }
          }

          if (error) {
            debugLog('RPC search error', error)
            // If model filter caused a function overload error, retry without model filter
            // and do client-side filtering instead
            if (options.compatibleModels && (error.message?.includes('could not choose') || error.message?.includes('structure of query'))) {
              debugLog('Retrying search without model filter, will filter client-side')
              const { data: fallbackData, error: fallbackError } = await supabase.rpc('search_sounds_ranked', {
                search_query: searchTerm,
                category_filter: typeof options.category === 'string' && options.category !== 'All' ? options.category : null,
                duration_min: durationMin,
                duration_max: durationMax,
                tesla_compatible: options.teslaCompatible || false,
                sort_by: options.sortBy || 'rank',
                sort_order: options.sortOrder || 'desc',
                result_limit: (options.limit || 50) * 2,
                result_offset: options.offset || 0,
              })
              if (fallbackError) {
                throw new SupabaseError(`Failed to search sounds: ${fallbackError.message}`, fallbackError)
              }
              const filtered = (fallbackData as Sound[])?.filter(
                s => s.compatible_models && s.compatible_models.includes(options.compatibleModels!)
              ).slice(0, options.limit || 50) || []
              debugLog('Client-side model filter applied', { original: fallbackData?.length, filtered: filtered.length })
              return normalizeSoundList(filtered)
            }
            throw new SupabaseError(`Failed to search sounds: ${error.message}`, error)
          }

          debugLog('RPC search successful', { recordCount: data?.length || 0 })
          const searchResults = data as Sound[]
          if (options.premiumOnly) {
            return normalizeSoundList(searchResults?.filter(s => s.is_premium) ?? [])
          }
          return normalizeSoundList(searchResults)
        }

        // Non-search query: use standard query builder
        debugLog('Building query...')
        const selectColumns = options.columns || '*'
        let query = supabase
          .from('sounds')
          .select(selectColumns)
          .eq('is_active', true)

        // Apply filters
        if (options.category && options.category !== 'All') {
          debugLog('Adding category filter', options.category)
          query = Array.isArray(options.category)
            ? query.in('category', options.category)
            : query.eq('category', options.category)
        }

        // Duration range filter
        if (options.durationRange && options.durationRange !== 'all') {
          debugLog('Adding duration range filter', options.durationRange)
          if (options.durationRange === '0-2') {
            query = query.gte('duration', 0).lte('duration', 2)
          } else if (options.durationRange === '2-5') {
            query = query.gt('duration', 2).lte('duration', 5)
          } else if (options.durationRange === '5+') {
            query = query.gt('duration', 5)
          }
        }

        // Tesla compatibility filter (duration <= 5s for Tesla lock sounds)
        if (options.teslaCompatible) {
          debugLog('Adding Tesla compatibility filter')
          query = query.lte('duration', 5)
        }

        // Note: fileSizeRange filtering is done client-side because 'size' is stored as string
        // This is acceptable since pagination reduces the dataset significantly

        if (options.volumeRange) {
          debugLog('Adding volume range filter', options.volumeRange)
          query = query
            .gte('volume_db', options.volumeRange[0])
            .lte('volume_db', options.volumeRange[1])
        }

        if (options.tags && options.tags.length > 0) {
          debugLog('Adding tags filter', options.tags)
          query = query.overlaps('tags', options.tags)
        }

        if (options.compatibleModels) {
          debugLog('Adding compatible_models filter', options.compatibleModels)
          query = query.overlaps('compatible_models', [options.compatibleModels])
        }

        if (options.premiumOnly) {
          debugLog('Adding is_premium filter')
          query = query.eq('is_premium', true)
        }

        // Apply sorting
        const sortBy = options.sortBy || 'created_at'
        const sortOrder = options.sortOrder || 'desc'
        debugLog('Adding sorting', { sortBy, sortOrder })

        if (sortBy === 'title') {
          query = query
            .order('title', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else if (sortBy === 'downloads') {
          query = query
            .order('downloads', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else if (sortBy === 'duration') {
          query = query
            .order('duration', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else if (sortBy === 'volume_db') {
          query = query
            .order('volume_db', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        } else {
          query = query
            .order('created_at', { ascending: sortOrder === 'asc' })
            .order('id', { ascending: true })
        }

        // Apply pagination (offset + limit) or just limit
        if (options.offset !== undefined && options.offset > 0) {
          const limit = options.limit || 50  // Default limit when using offset
          debugLog('Adding pagination', { offset: options.offset, limit })
          query = query.range(options.offset, options.offset + limit - 1)
        } else if (options.limit) {
          debugLog('Adding limit', options.limit)
          query = query.limit(options.limit)
        }

        debugLog('Executing query...')
        const { data, error } = await query

        if (error) {
          debugLog('Query error', error)
          throw new SupabaseError(`Failed to fetch sounds: ${error.message}`, error)
        }

        debugLog('Query successful', { recordCount: data?.length || 0 })

        // Validate each sound object
        const validatedSounds = ((data as unknown as Sound[]) || []).map((sound, index) => {
          const validationErrors: string[] = []

          if (!sound.id || typeof sound.id !== 'string') {
            validationErrors.push('Invalid or missing id')
          }
          if (!sound.title || typeof sound.title !== 'string') {
            validationErrors.push('Invalid or missing title')
          }
          if (!sound.file_url || typeof sound.file_url !== 'string') {
            validationErrors.push('Invalid or missing file_url')
          }

          if (validationErrors.length > 0) {
            if (isDev) console.log(`[soundsApi.getSounds] Sound validation failed at index ${index}:`, {
              sound,
              errors: validationErrors
            })
            debugLog(`Validation errors for sound ${index}`, validationErrors)
          }

          return normalizeSoundAudioUrls(sound)
        })

        debugLog('Validation complete', {
          totalSounds: validatedSounds.length,
          sampleSound: validatedSounds[0] ? {
            id: validatedSounds[0].id,
            title: validatedSounds[0].title,
            file_url: validatedSounds[0].file_url
          } : 'No sounds'
        })

        return validatedSounds
      })
    } catch (error) {
      if (!isStatementTimeout(error) || shouldReportTransientOnce('getSounds', error)) {
        Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSounds' } })
      }
      if (isDev) debugLog('Error occurred', error)
      // ISR callers pass throwOnError: true so a transient failure rejects the
      // page's Promise.all and Next.js keeps the last good cached render rather
      // than caching an empty list (ISR poisoning). Client-side callers leave
      // throwOnError unset (falsy) and get the original [] fallback.
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  // Get total count of sounds matching filters (efficient count query)
  async getSoundsCount(options: {
    category?: string
    search?: string
    durationRange?: 'all' | '0-2' | '2-5' | '5+'
    teslaCompatible?: boolean
    compatibleModels?: string
    premiumOnly?: boolean
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning 0. */
    throwOnError?: boolean
  } = {}): Promise<number> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // If search is provided, use the FTS count RPC
      if (options.search && options.search.trim()) {
        const searchTerm = options.search.trim().replace(/[<>]/g, '')

        // Calculate duration range values for RPC
        let durationMin: number | null = null
        let durationMax: number | null = null
        if (options.durationRange === '0-2') {
          durationMin = 0
          durationMax = 2
        } else if (options.durationRange === '2-5') {
          durationMin = 2
          durationMax = 5
        } else if (options.durationRange === '5+') {
          durationMin = 5
        }

        // Bound once so this closure keeps the null-narrowing from the guard above.
        const db = supabase
        const runCount = (term: string) =>
          db.rpc('search_sounds_count', {
            search_query: term,
            category_filter: typeof options.category === 'string' && options.category !== 'All' ? options.category : null,
            duration_min: durationMin,
            duration_max: durationMax,
            tesla_compatible: options.teslaCompatible || false,
            p_model_filter: options.compatibleModels || null
          })

        const { data, error } = await runCount(searchTerm)

        if (error) {
          throw new SupabaseError(`Failed to get sounds count: ${error.message}`, error)
        }

        // The count half of the TES-2229 fallback. It has to use the same gate
        // and the same stripped term as the list half in `getSounds`, or the
        // library reports "0 sounds" over a populated grid.
        if (((data as number) || 0) === 0) {
          const strippedTerm = stripFillerWords(searchTerm)
          if (strippedTerm) {
            const retried = await runCount(strippedTerm)
            if (!retried.error) return (retried.data as number) || 0
          }
        }

        return (data as number) || 0
      }

      // Non-search count: use standard query
      let query = supabase
        .from('sounds')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true)

      // Apply filters (same as getSounds)
      if (options.category && options.category !== 'All') {
        query = query.eq('category', options.category)
      }

      if (options.durationRange && options.durationRange !== 'all') {
        if (options.durationRange === '0-2') {
          query = query.gte('duration', 0).lte('duration', 2)
        } else if (options.durationRange === '2-5') {
          query = query.gt('duration', 2).lte('duration', 5)
        } else if (options.durationRange === '5+') {
          query = query.gt('duration', 5)
        }
      }

      if (options.teslaCompatible) {
        query = query.lte('duration', 5)
      }

      if (options.compatibleModels) {
        query = query.overlaps('compatible_models', [options.compatibleModels])
      }

      if (options.premiumOnly) {
        query = query.eq('is_premium', true)
      }

      const { count, error } = await query

      if (error) {
        throw new SupabaseError(`Failed to get sounds count: ${error.message}`, error)
      }

      return count || 0
    } catch (error) {
      if (!isClientNetworkFailure(error)) {
        Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundsCount' } })
      }
      if (shouldRethrow(options.throwOnError)) throw error
      return 0
    }
  },

  // Get sounds by category (legacy method)
  async getSoundsByCategory(category: string, options: { throwOnError?: boolean } = {}): Promise<Sound[]> {
    return this.getSounds({ category, throwOnError: options.throwOnError })
  },

  // Search sounds with enhanced FTS capabilities (searches title, description, and tags with ranking)
  async searchSounds(query: string, options: {
    category?: string
    tags?: string[]
  } = {}): Promise<Sound[]> {
    const sanitizedQuery = query.replace(/[<>]/g, '').trim()
    if (!sanitizedQuery || sanitizedQuery.length > 100) {
      return []
    }

    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // Use the ranked FTS search RPC
        const { data, error } = await supabase.rpc('search_sounds_ranked', {
          search_query: sanitizedQuery,
          category_filter: typeof options.category === 'string' && options.category !== 'All' ? options.category : null,
          duration_min: null,
          duration_max: null,
          tesla_compatible: false,
          sort_by: 'rank',
          sort_order: 'desc',
          result_limit: 50,
          result_offset: 0
        })

        if (error) {
          throw new SupabaseError(`Failed to search sounds: ${error.message}`, error)
        }

        return normalizeSoundList(data as Sound[])
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'searchSounds' } })
      return []
    }
  },

  // Get single sound
  async getSound(id: string, options: { throwOnError?: boolean } = {}): Promise<Sound | null> {
    Sentry.addBreadcrumb({
      category: 'soundsApi.getSound',
      message: 'getSound:start',
      level: 'info',
      data: {
        id,
        idLength: id?.length,
        looksLikeUuid: typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id),
        runtime: typeof process !== 'undefined' ? process.env.NEXT_RUNTIME ?? 'node' : 'unknown',
        supabaseInitialized: Boolean(supabase),
      },
    })
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('*')
          .eq('id', id)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            Sentry.addBreadcrumb({
              category: 'soundsApi.getSound',
              message: 'getSound:not-found',
              level: 'info',
              data: { id, code: error.code },
            })
            return null
          }
          throw new SupabaseError(`Failed to fetch sound: ${error.message}`, error)
        }

        return normalizeSoundAudioUrls(data as Sound)
      })
    } catch (error) {
      const err = error as { message?: string; code?: string; details?: string; hint?: string; status?: number }
      Sentry.captureException(error, {
        tags: { context: 'soundsApi', action: 'getSoundById' },
        extra: {
          id,
          errorMessage: err?.message,
          errorCode: err?.code,
          errorDetails: err?.details,
          errorHint: err?.hint,
          errorStatus: err?.status,
          runtime: typeof process !== 'undefined' ? process.env.NEXT_RUNTIME ?? 'node' : 'unknown',
        },
      })
      // ISR callers (app/sounds/[id]) pass throwOnError: true so a transient
      // failure rejects the render and Next keeps the last good snapshot,
      // instead of a null that becomes notFound() and is cached as a 404 for
      // the whole revalidate window. A real not-found (PGRST116) still
      // returns null above.
      if (shouldRethrow(options.throwOnError)) throw error
      return null
    }
  },

  // Batch fetch by IDs (e.g. saved-sounds hydration). Returns only found rows,
  // in no guaranteed order — callers re-order. Caps at 100 ids defensively.
  async getSoundsByIds(ids: string[]): Promise<Sound[]> {
    if (ids.length === 0) return []
    const capped = ids.slice(0, 100)
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized')
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('*')
          .in('id', capped)

        if (error) {
          throw new SupabaseError(`Failed to batch fetch sounds: ${error.message}`, error)
        }

        return (data as Sound[]).map(normalizeSoundAudioUrls)
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'soundsApi', action: 'getSoundsByIds' },
        extra: {
          idCount: capped.length,
        },
      })
      return []
    }
  },

  async getSoundBySlug(slug: string, options: { throwOnError?: boolean } = {}): Promise<Sound | null> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('*')
          .eq('slug', slug)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            return null
          }
          throw new SupabaseError(`Failed to fetch sound by slug: ${error.message}`, error)
        }

        return normalizeSoundAudioUrls(data as Sound)
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundBySlug' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return null
    }
  },

  async getSoundByShortId(shortId: string): Promise<string | null> {
    if (!/^[0-9a-f]+$/i.test(shortId)) return null
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('sounds')
          .select('id')
          .like('id', `${shortId}-%`)
          .limit(1)

        if (error) {
          throw new SupabaseError(`Failed to lookup short ID: ${error.message}`, error)
        }

        if (!data || data.length === 0) {
          return null
        }

        return data[0].id as string
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundByShortId' } })
      return null
    }
  },

  // Get featured sounds with category diversity for hero carousel
  async getFeaturedSounds(limit: number = 8): Promise<Sound[]> {
    try {
      // Fetch extra sounds to allow for category diversity and randomization
      const sounds = await this.getSounds({ sortBy: 'downloads', sortOrder: 'desc', limit: limit * 2 })

      // Category-diverse selection — shared with the client TrendingSection
      // (lib/featured-sounds.ts) so server-rendered and hydrated rails match.
      return pickDiverseSounds(sounds, limit)
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getFeaturedSounds' } })
      return []
    }
  },

  // Get sounds by Tesla model compatibility. `compatibleModels` is the option
  // the query builder actually applies — the old `compatibility` key was never
  // read, so this returned the whole active catalog with '*' (~10 MB) for a
  // caller that renders 24 rows, and the model/year pages timed out at build.
  async getSoundsByTeslaModel(
    modelName: string,
    options: { limit?: number; throwOnError?: boolean } = {},
  ): Promise<Sound[]> {
    return this.getSounds({
      compatibleModels: modelName,
      limit: options.limit,
      sortBy: 'downloads',
      sortOrder: 'desc',
      columns: LIST_COLUMNS,
      throwOnError: options.throwOnError,
    })
  },

  // Get sounds by collection (database-based)
  async getSoundsByCollection(collectionId: string): Promise<Sound[]> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('collection_items')
          .select('sounds!inner(*)')
          .eq('collection_id', collectionId)
          .eq('sounds.is_active', true);

        if (error) {
          throw new SupabaseError(`Failed to fetch sounds for collection: ${error.message}`, error);
        }

        return normalizeSoundList(data?.flatMap((item: any) => item.sounds ? [item.sounds] : []) as Sound[] || []);
      });
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundsByCollection' } })
      return [];
    }
  },

  // Get sounds by curated collection (search term based)
  async getSoundsByCuratedCollection(collectionId: string, options?: { limit?: number }): Promise<Sound[]> {
    // Import dynamically to avoid circular dependency
    const { getCuratedCollection } = await import('../curated-collections')
    const collection = getCuratedCollection(collectionId)

    if (!collection) {
      return []
    }

    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const orFilters = curatedSearchTermFilter(collection.searchTerms)

        const { data, error } = await supabase
          .from('sounds')
          // LIST_COLUMNS (not '*') drops waveform_data — up to 200 of these are
          // serialized into the client CollectionSortGrid, so the large JSON
          // arrays dominated this page-type's LCP payload + hydration TBT
          // (TES-834). AudioWaveform regenerates waveforms from soundId.
          .select(LIST_COLUMNS)
          // Deactivated rows are merged duplicates: /sounds/[id] either 308s them
          // to a live replacement or serves them noindex (see app/sounds/[id]/page.tsx).
          // Without this filter every curated collection rendered them as normal
          // cards — on kids-family that was 36 of 82 matches (44%) pointing at
          // redirect/noindex URLs from a page whose whole job is to rank.
          .eq('is_active', true)
          .or(orFilters)
          .order('downloads', { ascending: false })
          .limit(options?.limit || 50)

        if (error) {
          throw new SupabaseError(`Failed to fetch sounds for curated collection: ${error.message}`, error)
        }

        return normalizeSoundList(data as Sound[])
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getSoundsByCuratedCollection' } })
      return []
    }
  },

  /**
   * How many ACTIVE sounds each curated collection actually holds, keyed by id.
   *
   * Counts the same sounds `getSoundsByCuratedCollection` lists, so the number
   * a listing shows can never drift from the page it links to.
   * `curatedCollections[].estimatedCount` is an estimate and must never reach a
   * user-facing surface (docs/ANTI_SLOP.md).
   *
   * ## Why this is one RPC and not one query per collection (TES-2142)
   *
   * This used to ask PostgREST for `count=exact` under `curatedSearchTermFilter`'s
   * `or=`, once per collection. Sci-Fi Classics is 43 terms, so that is 86
   * unanchored `ILIKE` predicates over every row — 284 ms of pure CPU, and the
   * planner will not use a trgm index for an OR that wide (it takes the index
   * at ~20 predicates and abandons it at 86). Thirteen of those in parallel per
   * homepage render hit the DB statement timeout at ~10 concurrent renders.
   *
   * `count_curated_collections` runs one nested loop per term instead, so each
   * inner scan sees a single `ILIKE` pair and takes the trgm index whatever the
   * term count — and answers for all thirteen collections in one round trip.
   * Measured on prod: 10 concurrent renders went from p100 10.7 s with ten
   * `57014`s to p100 532 ms with none.
   *
   * The search terms are passed in rather than stored in the database, so
   * `lib/curated-collections.ts` stays the only place a collection is defined.
   *
   * An id missing from the result has no established count, and the caller
   * renders no count at all for it — "0 sounds" would be a claim this doesn't
   * know to be true. Returns {} when there is nothing to ask (no Supabase
   * client at build time, no recognised id) or on the expected statement
   * timeout. Any other failure REJECTS, so the one caller
   * (`getDisplayCollectionCounts`) reports it once for the whole batch.
   */
  async getCuratedCollectionCounts(collectionIds: string[]): Promise<Record<string, number>> {
    if (collectionIds.length === 0 || !supabase) return {}

    const { getCuratedCollection } = await import('../curated-collections')
    const collections = collectionIds.flatMap(id => {
      const collection = getCuratedCollection(id)
      return collection ? [{ slug: id, terms: collection.searchTerms }] : []
    })
    if (collections.length === 0) return {}

    try {
      const { data, error, status, statusText } = await supabase.rpc(
        'count_curated_collections',
        { collections },
      )

      if (error) {
        throw new SupabaseError(
          `Failed to count curated collections: ${describeSupabaseFailure(error, status, statusText)}`,
          error,
        )
      }

      const counts: Record<string, number> = {}
      for (const row of (data ?? []) as { slug: string; sound_count: number }[]) {
        if (typeof row?.sound_count === 'number') counts[row.slug] = row.sound_count
      }
      return counts
    } catch (error) {
      // Still the expected failure under enough load, just far further out —
      // the caller renders countless rows, which is the whole degrade path.
      if (isStatementTimeout(error)) return {}
      throw error
    }
  },

  // Get sounds by volume range
  async getSoundsByVolumeRange(min: number, max: number): Promise<Sound[]> {
    return this.getSounds({ volumeRange: [min, max] })
  },

  async incrementDownloads(id: string): Promise<void> {
    return soundsApi.accountDownload({ soundId: id })
  },

  async accountDownload(options: { soundId?: string | null } = {}): Promise<void> {
    const path = downloadAccountPath(downloadAccountTarget(options.soundId))
    try {
      const res = await fetch(path, { method: 'POST' })
      if (res.status === 429) return
      if (!res.ok && res.status !== 204) {
        throw new Error(`Download increment failed: ${res.status}`)
      }
    } catch (error) {
      if (isDownloadRateLimited(error) || isClientNetworkFailure(error)) return
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'incrementDownloads' } })
    }
  },

  // Increment like count
  async incrementLikes(_id: string): Promise<void> {
    // NOTE: likes field doesn't exist in database - this is a no-op
    // Will need to add likes field to database schema if we want to enable this
  },

  // Get categories
  async getCategories(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning []. */
    throwOnError?: boolean
  } = {}): Promise<Category[]> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name') // Sort by name instead of non-existent sort_order

        if (error) {
          throw new SupabaseError(`Failed to fetch categories: ${error.message}`, error)
        }

        return (data as Category[]) || []
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getCategories' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return []
    }
  },

  async getCategoryCounts(options: {
    /** ISR pages pass true so transient errors rethrow (keeps last good cache) instead of returning {}. */
    throwOnError?: boolean
  } = {}): Promise<Record<string, number>> {
    try {
      return await withRetry(async () => {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // Aggregated in Postgres (supabase/migrations/20260920_active_category_counts.sql)
        // rather than folded here. A `select('category').eq('is_active', true)`
        // returns at most `db-max-rows` (1000) with a 200 and no error, so the
        // fold silently counted 1000 of 1767 active sounds; paging around that
        // was correct but shipped the whole active catalogue per call, and `/`
        // is force-dynamic. One row per category can never reach the cap.
        const { data, error } = await supabase.rpc('get_active_category_counts')

        if (error) {
          throw new SupabaseError(`Failed to fetch category counts: ${error.message}`, error)
        }

        const counts: Record<string, number> = {}
        for (const row of (data as { category: string; sound_count: number }[] | null) || []) {
          counts[row.category] = Number(row.sound_count)
        }
        return counts
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getCategoryCounts' } })
      if (shouldRethrow(options.throwOnError)) throw error
      return {}
    }
  },

  // Get aggregated stats for hero section
  async getHeroStats(): Promise<{
    totalSounds: number
    totalCategories: number
    latestTeslaYear: number
    totalDownloads: number
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const [soundsResult, categoriesResult, downloadsResult] = await Promise.all([
        supabase.from('sounds').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('categories').select('id', { count: 'exact', head: true }),
        supabase.rpc('get_total_downloads')
      ])

      const totalSounds = soundsResult.count || 0
      const totalCategories = categoriesResult.count || 0
      const totalDownloads = (downloadsResult.data as number | null) || 0

      return {
        totalSounds,
        totalCategories,
        latestTeslaYear: 2025, // Latest compatible Tesla year
        totalDownloads
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'soundsApi', action: 'getHeroStats' } })
      return {
        totalSounds: 0,
        totalCategories: 0,
        latestTeslaYear: 2025,
        totalDownloads: 0
      }
    }
  }
}
